import { action, computed, observable } from 'mobx'
import { orderBy } from 'lodash-es'
import { defer, type Observable } from 'rxjs'
import { type IValidationSchema, required, validationContext } from 'validx'
import type { LegendPartDependencies } from '@/features/legends/editor/legend-part-dependencies'
import { LegendPartViewModelBase } from '@/features/legends/editor/LegendPartViewModelBase'
import {
  type ErrorDescriptor,
  receiveErrors,
} from '@/features/legends/editor/analyzer-errors'
import type {
  EntityDescriptor,
  SymbolContext,
} from '@/features/legends/editor/entities'
import { newCid } from '@/features/legends/editor/collections'
import { Types } from '@/features/legends/editor/ryno-types'
import {
  type Validatable,
  validateFields,
  validateWhileEditing,
} from '@/features/legends/editor/validation'
import { ViewRequest } from '@/features/legends/editor/view-request'
import { tapAction, toStream } from '@/utils/mobx-util'
import { when } from '@/utils/validx-util'

/**
 * Decides whether a connector offers an entity.
 */
export type SymbolPredicate = (
  descriptor: EntityDescriptor,
  connector: SymbolConnectorViewModel,
) => boolean

export interface SymbolConnectorViewModelOptions {
  dependencies: LegendPartDependencies
  context: SymbolContext
  predicates?: SymbolPredicate[]
  required?: boolean
}

/**
 * A reference to one entity declared earlier in the legend, like the question
 * a filter checks or the variable a mutator changes. Serializes to the ID.
 */
export class SymbolConnectorViewModel extends LegendPartViewModelBase {
  private readonly _context: SymbolContext
  private readonly _predicates: SymbolPredicate[]
  private readonly _isRequired: boolean
  private readonly _schema: IValidationSchema<SymbolConnectorViewModel> = {
    symbol: [when(() => this._isRequired, required('Please select a symbol.'))],
  }
  private readonly _focusRequest = new ViewRequest()

  /**
   * Identifies the connector on the client, for keys.
   */
  readonly cid = newCid('connector')

  @observable accessor symbolId: string | null = null

  /**
   * The text in the picker's input: the picked ID, or what is being searched.
   */
  @observable accessor searchText = ''

  readonly validation = validationContext(this, this._schema)

  /**
   * Emits when something asks to focus the picker, like adding a mutator.
   */
  readonly focus$ = this._focusRequest.request$

  constructor({
    dependencies,
    context,
    predicates,
    required: isRequired,
  }: SymbolConnectorViewModelOptions) {
    super(dependencies)
    this._context = context
    this._predicates = predicates ?? []
    this._isRequired = !!isRequired
    this.lifecycle.manage(validateWhileEditing(this, this._schema))
  }

  /**
   * The entity `symbolId` refers to, if it is still available.
   */
  @computed
  get symbol() {
    if (!this.symbolId) {
      return null
    }
    return (
      this.availableEntityDescriptors.find((x) => x.entity.id === this.symbolId)
        ?.entity ?? null
    )
  }

  /**
   * The entities that can be picked here, by ID.
   */
  @computed
  get availableEntityDescriptors(): EntityDescriptor[] {
    const descriptors = this.dependencies.symbols
      .getAvailableEntityDescriptorsFor(this._context)
      .filter((e) => !e.hidden && this._predicates.every((p) => p(e, this)))
    return orderBy(descriptors, (x) => x.entity.id)
  }

  /**
   * The picked entity's type.
   */
  @computed
  get valueType() {
    return this.symbol?.valueType ?? Types.nothing
  }

  /**
   * Sets the search text.
   */
  @action.bound
  setSearchText(value: string | null | undefined) {
    this.searchText = value ?? ''
  }

  /**
   * Clears the search text, so that every entity is listed.
   */
  @action.bound
  clearSearch() {
    this.searchText = ''
  }

  /**
   * Picks the entity, or clears the pick.
   */
  @action.bound
  setSymbolId(id: string | null | undefined) {
    this.symbolId = id || null
    this.searchText = id || ''
  }

  /**
   * Asks the picker to take focus and open.
   */
  focus() {
    this._focusRequest.request()
  }

  /**
   * Sets the referenced ID from the legend document.
   */
  @action
  hydrate(symbol: string | null | undefined) {
    this.setSymbolId(symbol)
  }

  /**
   * Revalidates the target's field when the referenced symbol or its type
   * changes, while the returned stream is subscribed; pass it to the
   * target's lifecycle. A symbol that is already set is checked when the
   * stream is subscribed, so a loaded target is checked against it.
   *
   * @param target
   * @param schema The schema the target's validation context is bound to.
   * @param field
   */
  bubbleErrorsTo<T>(
    target: T & Validatable,
    schema: IValidationSchema<T>,
    field: keyof T & string,
  ): Observable<unknown> {
    return defer(() =>
      toStream(
        () => this.symbol && [this.symbol.id, this.symbol.valueType],
        !!this.symbolId,
      ),
    ).pipe(tapAction(() => validateFields(target, schema, [field])))
  }

  /**
   * Shows the analyzer's errors for the reference.
   */
  receiveErrors(descriptor: ErrorDescriptor) {
    receiveErrors(this, descriptor.inner)
  }

  /**
   * The referenced ID, when the entity is still available.
   */
  toJSON(): string | null {
    return this.symbol ? this.symbol.id : null
  }
}
